'use client';

import * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { sanitizeNumericInput } from '../utils/number-formatting';
import { Input } from './input';

export interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
    /** Current numeric value (can be injected by parent via Slot) */
    value?: number;
    onChange?: (value: number | undefined) => void;
    /** Initial value for type-to-enter mode (e.g., '-', '.', '5') */
    initialValue?: string;
    step?: number;
    incrementButtons?: boolean;
    allowNegative?: boolean;
    allowDecimal?: boolean;
    maxDecimals?: number;
    min?: number;
    max?: number;
    ref?: React.Ref<HTMLInputElement>;
}

export function NumberInput({
    value,
    onChange,
    initialValue,
    step = 1,
    incrementButtons = false,
    allowNegative = false,
    allowDecimal = true,
    maxDecimals,
    className,
    onKeyDown,
    ref,
    min,
    max,
    ...props
}: NumberInputProps) {
    const clampToBounds = React.useCallback(
        (n: number) => {
            let next = n;
            if (Number.isFinite(min) && next < (min as number)) next = min as number;
            if (Number.isFinite(max) && next > (max as number)) next = max as number;
            // Respect allowNegative in case min isn't provided
            if (!allowNegative && next < 0) next = 0;
            return next;
        },
        [allowNegative, max, min],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(e);

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            handleIncrement();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            handleDecrement();
            return;
        }

        // Let all other keys through - sanitization happens in onChange
    };

    // Initialize with initial value if provided, otherwise use prop value
    const [localValue, setLocalValue] = React.useState(() => {
        if (initialValue !== undefined) {
            // Sanitize initial value to remove non-numeric characters
            return sanitizeNumericInput(initialValue, allowNegative, allowDecimal);
        }
        if (value !== undefined) return value.toString();
        return '';
    });

    // Track if we're in "initial typing" mode to prevent premature sync
    const isInitialTyping = React.useRef(!!initialValue);

    // Sync local value with prop value when it changes externally
    // Don't sync from prop while user is actively typing with initial character
    React.useEffect(() => {
        if (isInitialTyping.current) {
            return;
        }

        // Don't overwrite localValue if it already represents the correct value
        // (e.g., '12.' and 12 are the same value, just different representations)
        const parsed = parseFloat(localValue);
        if (!isNaN(parsed) && parsed === value) {
            return;
        }

        setLocalValue(value === undefined ? '' : value.toString());
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!onChange) return;
        const input = e.target.value;

        // User is now actively typing - clear initial typing mode
        isInitialTyping.current = false;

        // Keep what the sanitizer salvages rather than rejecting the whole entry. Typing a stray
        // character still lands on the same value it always did, because the character is simply
        // dropped, and a pasted "$1,234.56" now works instead of being discarded wholesale.
        // Assumes US formatting: a comma is a thousands separator, never a decimal point.
        const sanitized = sanitizeNumericInput(input, allowNegative, allowDecimal);

        setLocalValue(sanitized);

        if (sanitized === '' || sanitized === '-') {
            onChange(undefined);
            return;
        }

        const parsed = parseFloat(sanitized);

        if (!isNaN(parsed)) {
            if (maxDecimals !== undefined && allowDecimal) {
                const decimals = (sanitized.split('.')[1] || '').length;
                if (decimals <= maxDecimals) {
                    const next = clampToBounds(parsed);
                    onChange(next);

                    // Only update if clamping changed the value
                    if (next !== parsed) {
                        setLocalValue(next.toString());
                    }
                } else {
                    // Reject - revert display to previous value
                    setLocalValue(value === undefined ? '' : value.toString());
                }
            } else {
                const next = clampToBounds(parsed);
                onChange(next);

                // Only update if clamping changed the value
                if (next !== parsed) {
                    setLocalValue(next.toString());
                }
            }
        } else if (sanitized === '' || sanitized === '-') {
            onChange(undefined);
        }
    };

    const handleIncrement = () => {
        if (!onChange) return;
        const currentValue = value ?? 0;
        const next = clampToBounds(currentValue + step);
        onChange(next);
        setLocalValue(next.toString());
    };

    const handleDecrement = () => {
        if (!onChange) return;
        const currentValue = value ?? 0;
        const next = clampToBounds(currentValue - step);
        onChange(next);
        setLocalValue(next.toString());
    };

    return (
        <div className="flex size-full items-center">
            <Input
                ref={ref}
                type="text"
                inputMode="decimal"
                value={localValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className={cn(
                    'h-full min-h-5 w-full border-0 bg-transparent px-0 py-0 text-sm leading-5 shadow-none focus-visible:ring-0',
                    className,
                )}
                {...props}
            />
            {incrementButtons && (
                <div className="ml-1 flex flex-col">
                    <button
                        type="button"
                        onClick={handleIncrement}
                        className="flex h-3 w-4 items-center justify-center text-xs leading-none hover:bg-gray-100"
                        tabIndex={-1}
                    >
                        ▲
                    </button>
                    <button
                        type="button"
                        onClick={handleDecrement}
                        className="flex h-3 w-4 items-center justify-center text-xs leading-none hover:bg-gray-100"
                        tabIndex={-1}
                    >
                        ▼
                    </button>
                </div>
            )}
        </div>
    );
}

NumberInput.displayName = 'NumberInput';
